import { useState, useEffect, useCallback, useMemo } from "react";
import { DEFAULTS } from "../constants/defaults";

const EMPTY_SUMMARY = {
  emi: 0,
  totalPayment: 0,
  totalInterest: 0,
  totalPrincipal: 0,
  totalExtraPayments: 0,
  totalLumpsumPrepayments: 0,
  monthsCompleted: 0,
  monthsSaved: 0,
  interestSaved: 0,
  rateChanges: 0,
  totalPayable: 0,
};

const useEMICalculation = () => {
  const [loanAmount, setLoanAmount] = useState(DEFAULTS.loanAmount);
  const [interestRate, setInterestRate] = useState(DEFAULTS.interestRate);
  const [loanPeriod, setLoanPeriod] = useState(DEFAULTS.loanPeriod);
  const [monthlyExtraPayment, setMonthlyExtraPayment] = useState(DEFAULTS.monthlyExtraPayment);
  const [monthlyExtraPaymentInput, setMonthlyExtraPaymentInput] = useState(DEFAULTS.monthlyExtraPayment);
  const [prepayments, setPrepayments] = useState([]);
  const [rateType, setRateType] = useState("fixed");
  const [rateSchedule, setRateSchedule] = useState([
    { fromMonth: 1, toMonth: DEFAULTS.loanPeriod, rate: DEFAULTS.interestRate },
  ]);
  const [newRateMonth, setNewRateMonth] = useState("");
  const [newRateValue, setNewRateValue] = useState("");
  const [newPrepayMonth, setNewPrepayMonth] = useState("");
  const [newPrepayAmount, setNewPrepayAmount] = useState("");
  const [errors, setErrors] = useState({});

  // Validate inputs and surface errors
  useEffect(() => {
    const newErrors = {};
    if (!loanAmount || loanAmount <= 0)
      newErrors.loanAmount = "Loan amount must be greater than 0";
    if (!interestRate || interestRate <= 0)
      newErrors.interestRate = "Interest rate must be greater than 0";
    else if (interestRate > 100)
      newErrors.interestRate = "Interest rate cannot exceed 100%";
    if (!loanPeriod || loanPeriod < 1)
      newErrors.loanPeriod = "Loan period must be at least 1 month";
    if (monthlyExtraPayment < 0)
      newErrors.monthlyExtraPayment = "Extra payment cannot be negative";
    setErrors(newErrors);
  }, [loanAmount, interestRate, loanPeriod, monthlyExtraPayment]);

  const calculateEMI = useCallback((principal, annualRate, months) => {
    if (months <= 0 || principal <= 0) return 0;
    const monthlyRate = annualRate / 12 / 100;
    if (monthlyRate === 0) return principal / months;
    const pow = Math.pow(1 + monthlyRate, months);   // compute once, reuse twice
    return (principal * monthlyRate * pow) / (pow - 1);
  }, []);

  const getRateForMonth = useCallback(
    (month) => {
      if (rateType === "fixed") return interestRate;
      const applicable = rateSchedule.find(
        (r) => month >= r.fromMonth && month <= r.toMonth
      );
      return applicable ? applicable.rate : interestRate;
    },
    [rateType, interestRate, rateSchedule]
  );

  const calculateNormalLoanInterest = useCallback(
    (principal, annualRate, months) => {
      const emi = calculateEMI(principal, annualRate, months);
      return emi * months - principal;
    },
    [calculateEMI]
  );

  // Keep rate schedule in sync with loan params, preventing infinite loops
  useEffect(() => {
    if (rateType === "fixed") {
      const needsUpdate =
        rateSchedule.length !== 1 ||
        rateSchedule[0].fromMonth !== 1 ||
        rateSchedule[0].toMonth !== loanPeriod ||
        rateSchedule[0].rate !== interestRate;
      if (needsUpdate) {
        setRateSchedule([{ fromMonth: 1, toMonth: loanPeriod, rate: interestRate }]);
      }
    } else if (
      rateSchedule.length === 1 &&
      rateSchedule[0].fromMonth === 1 &&
      rateSchedule[0].toMonth === loanPeriod &&
      rateSchedule[0].rate !== interestRate
    ) {
      setRateSchedule([{ fromMonth: 1, toMonth: loanPeriod, rate: interestRate }]);
    }
  }, [rateType, interestRate, loanPeriod, rateSchedule]);

  // Memoized amortization schedule — avoids recomputing on every render
  const { schedule, summary } = useMemo(() => {
    const isValid = loanAmount > 0 && interestRate > 0 && loanPeriod >= 1;
    if (!isValid) return { schedule: [], summary: EMPTY_SUMMARY };

    let remainingBalance = loanAmount;
    const newSchedule = [];
    const sortedPrepayments = [...prepayments].sort((a, b) => a.month - b.month);
    let prepaymentIndex = 0;
    let rateChangeCount = 0;
    let lastRate = null;
    let currentEMI = 0;

    // Accumulators — totals built in the main loop; no post-loop reduce passes needed
    let accInterest = 0;
    let accEMI = 0;
    let accPayment = 0;
    let accExtra = 0;
    let accLumpsum = 0;

    const initialRate = rateType === "fixed" ? interestRate : getRateForMonth(1);
    const normalLoanInterest = calculateNormalLoanInterest(loanAmount, initialRate, loanPeriod);

    for (let month = 1; month <= loanPeriod && remainingBalance > 0; month++) {
      const currentRate = getRateForMonth(month);
      const monthlyRate = currentRate / 12 / 100;

      if (lastRate !== null && lastRate !== currentRate) {
        rateChangeCount++;
        if (rateType === "floating") {
          const remainingMonths = loanPeriod - month + 1;
          currentEMI = calculateEMI(remainingBalance, currentRate, remainingMonths);
        }
      } else if (month === 1) {
        currentEMI = calculateEMI(loanAmount, currentRate, loanPeriod);
      }

      const interestForMonth = remainingBalance * monthlyRate;
      let principalForMonth = currentEMI - interestForMonth;

      if (principalForMonth > remainingBalance) {
        principalForMonth = remainingBalance;
        currentEMI = principalForMonth + interestForMonth;
      }

      let extraPaymentForMonth = 0;
      if (monthlyExtraPayment > 0) {
        const afterPrincipal = remainingBalance - principalForMonth;
        extraPaymentForMonth = afterPrincipal > monthlyExtraPayment
          ? monthlyExtraPayment
          : afterPrincipal > 0 ? afterPrincipal : 0;
      }

      let lumpsumPrepayment = 0;
      if (
        prepaymentIndex < sortedPrepayments.length &&
        sortedPrepayments[prepaymentIndex].month === month
      ) {
        lumpsumPrepayment = sortedPrepayments[prepaymentIndex].amount;
        prepaymentIndex++;
      }

      remainingBalance = Math.max(
        0,
        remainingBalance - principalForMonth - extraPaymentForMonth - lumpsumPrepayment
      );

      const rowPayment = currentEMI + extraPaymentForMonth + lumpsumPrepayment;
      accInterest += interestForMonth;
      accEMI      += currentEMI;
      accPayment  += rowPayment;
      accExtra    += extraPaymentForMonth;
      accLumpsum  += lumpsumPrepayment;

      newSchedule.push({
        month,
        rate: currentRate,
        emi: currentEMI,
        interest: interestForMonth,
        principal: principalForMonth,
        extraPayment: extraPaymentForMonth,
        lumpsumPrepayment,
        totalPayment: rowPayment,
        balance: remainingBalance,
        rateChanged: lastRate !== null && lastRate !== currentRate,
      });

      lastRate = currentRate;
      if (remainingBalance === 0) break;
    }

    const n = newSchedule.length;

    return {
      schedule: newSchedule,
      summary: {
        emi: n > 0 ? accEMI / n : 0,
        totalPayment: accPayment,
        totalInterest: accInterest,
        totalPrincipal: loanAmount,
        totalExtraPayments: accExtra,
        totalLumpsumPrepayments: accLumpsum,
        monthsCompleted: n,
        monthsSaved: loanPeriod - n,
        interestSaved: normalLoanInterest - accInterest,
        rateChanges: rateChangeCount,
        totalPayable: accInterest + loanAmount,
      },
    };
  }, [
    loanAmount,
    interestRate,
    loanPeriod,
    prepayments,
    monthlyExtraPayment,
    rateType,
    getRateForMonth,
    calculateNormalLoanInterest,
    calculateEMI,
  ]);

  const addFloatingRateChange = () => {
    if (!newRateMonth || !newRateValue) return;
    const month = parseInt(newRateMonth);
    const rate = parseFloat(newRateValue);
    if (!(month > 0 && month <= loanPeriod && rate > 0)) return;

    let updated = [...rateSchedule];
    const insertIndex = updated.findIndex(
      (r) => month >= r.fromMonth && month <= r.toMonth
    );

    if (insertIndex === -1) {
      const last = updated[updated.length - 1];
      if (month > last.toMonth) {
        updated.push({ fromMonth: month, toMonth: loanPeriod, rate });
      }
    } else {
      const existing = updated[insertIndex];
      if (month === existing.fromMonth) {
        updated.splice(insertIndex, 0, { fromMonth: month, toMonth: existing.toMonth, rate });
        updated[insertIndex + 1].toMonth = month - 1;
      } else {
        updated[insertIndex] = { ...existing, toMonth: month - 1 };
        updated.splice(insertIndex + 1, 0, { fromMonth: month, toMonth: existing.toMonth, rate });
      }
    }

    setRateSchedule(
      updated.filter((r) => r.fromMonth <= r.toMonth).sort((a, b) => a.fromMonth - b.fromMonth)
    );
    setNewRateMonth("");
    setNewRateValue("");
  };

  const removeFloatingRatePeriod = (index) => {
    const updated = rateSchedule.filter((_, i) => i !== index);
    if (updated.length > 0) setRateSchedule(updated);
  };

  const resetToFixedRate = () => {
    setRateSchedule([{ fromMonth: 1, toMonth: loanPeriod, rate: interestRate }]);
    setRateType("fixed");
  };

  const addPrepayment = () => {
    if (!newPrepayMonth || !newPrepayAmount) return;
    const month = parseInt(newPrepayMonth);
    const amount = parseFloat(newPrepayAmount);
    if (!isNaN(month) && !isNaN(amount) && month > 0 && month <= loanPeriod && amount > 0) {
      setPrepayments([...prepayments, { month, amount }]);
      setNewPrepayMonth("");
      setNewPrepayAmount("");
    }
  };

  const removePrepayment = (index) => {
    setPrepayments(prepayments.filter((_, i) => i !== index));
  };

  const handleMonthlyExtraPaymentKeyPress = (e) => {
    if (e.key === "Enter") {
      if (monthlyExtraPaymentInput >= 0) {
        setMonthlyExtraPayment(monthlyExtraPaymentInput);
      }
    }
  };

  return {
    loanAmount, setLoanAmount,
    interestRate, setInterestRate,
    loanPeriod, setLoanPeriod,
    monthlyExtraPayment, setMonthlyExtraPayment,
    monthlyExtraPaymentInput, setMonthlyExtraPaymentInput,
    prepayments, setPrepayments,
    rateType, setRateType,
    rateSchedule,
    newRateMonth, setNewRateMonth,
    newRateValue, setNewRateValue,
    newPrepayMonth, setNewPrepayMonth,
    newPrepayAmount, setNewPrepayAmount,
    errors,
    schedule,
    summary,
    addFloatingRateChange,
    removeFloatingRatePeriod,
    resetToFixedRate,
    addPrepayment,
    removePrepayment,
    handleMonthlyExtraPaymentKeyPress,
  };
};

export default useEMICalculation;
